import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Layout, { SecondaryCTA } from '@pcid/components/layout';
import Sprite from '@pcid/sprite';
import { AutoFormattedMessage } from '@pcid/string-utils';
import { getStoreName } from '@pcid/components/config-context';
import Button from '@pcid/components/button';
import { getKmsiEmailStorage, getKmsiTokenStorage, getLoginCtxStorage, getRelyingPartyStorage } from '@pcid/storage-utils';
import Link from '@pcid/link';
import LoadingPage from '@pcid/components/loading-page';
import { parseLanguage } from '@pcid/locale-utils';
import { withLocale } from '@pcid/components/locale-provider';

import { processGetUser, processKmsiLogin } from '../actions/login';
import { processKmsiLogout } from '../actions/logout';
import { trackContinueAsPageView, trackSsoLoginSubmit } from '../actions/track-analytics';

const ContinueAsPage = ({ locale }) => {
	const [user, setUser] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);

	useEffect(() => {
		processGetUser().then((userData) => setUser(userData));
	}, []);

	const relyingParty = getRelyingPartyStorage().get();
	const loginCtx = getLoginCtxStorage().getIn(relyingParty);

	const haveLongEmail = () => user.email.length > 30;
	const useDifferentAccount = () => {
		getKmsiTokenStorage().remove();
		getKmsiEmailStorage().remove();
		processKmsiLogout();
	};

	const convergenceBanner = (
		<div className="login-form__convergence-banner-bottom">
			<AutoFormattedMessage
				id="form.createAccount.description1"
			/>
			<hr className="login-form__divider" />
			<Sprite
				group="banner"
				symbol={`banner-optimum_${locale}`}
				className="login-form__convergence-banner-svg"
			/>
			<Sprite
				group="banner"
				symbol={`banner-express_${locale}`}
				className="login-form__convergence-banner-svg"
			/>
			<Sprite
				group="banner"
				symbol={`banner-health_${locale}`}
				className="login-form__convergence-banner-svg"
			/>
			<span className="login-form__last-three-svg-group">
				<Sprite
					group="banner"
					symbol={`banner-shoppers_${locale}`}
					className="login-form__convergence-banner-svg"
				/>
				<Sprite
					group="banner"
					symbol={`banner-lifemark_${locale}`}
					className="login-form__convergence-banner-svg"
				/>
				<Sprite
					group="banner"
					symbol="banner-joe-fresh"
					className="login-form__convergence-banner-svg login-form__last-svg"
				/>
			</span>
		</div>
	);

	if (!user) {
		return <LoadingPage />;
	// eslint-disable-next-line no-else-return
	} else {
		trackContinueAsPageView();
		return (
			<Layout
				title="page.title.continueAs"
				className="continue-as-page"
				convergenceBanner={convergenceBanner}
				convergenceBannerPosition="bottom"
			>
				<div>
					<Sprite
						group="banners"
						symbol={`pc-id_${locale}`}
						alt="PCid Logo"
						className="layout__pcid-logo-banner"
					/>
					<h2 className="continue-as-page__heading">
						<AutoFormattedMessage
							id="page.continueAs.heading"
						/>
					</h2>
				</div>
				<div className="continue-as-page__description">
					<div>
						<AutoFormattedMessage
							id="page.continueAs.description"
						/>
					</div>
					<div>
						{getStoreName()
						&& (
							<AutoFormattedMessage
								id="page.continueAs.descriptionStoreName"
								values={{ storeName: <AutoFormattedMessage id={getStoreName()} /> }}
							/>
						)}
					</div>
				</div>
				<Button
					className="continue-as-page__submit-button"
					submitting={isLoading}
					disabled={isDisabled}
					onClick={() => {
						trackSsoLoginSubmit();
						processKmsiLogin({ loginCtx, user });
						setIsLoading(true);
						setIsDisabled(true);
					}}
				>
					<Sprite
						group="icons"
						symbol="sso-email-icon"
						className="continue-as-page__submit-button-email-icon"
					/>
					{user.email}
				</Button>
				{haveLongEmail()
					? (
						<div className="continue-as-page__hidden-email">
							<div className="continue-as-page__hidden-email-container">
								<div className="continue-as-page__hidden-email-triangle" />
								<div className="continue-as-page__hidden-email-display">
									<p className="continue-as-page__hidden-email-text">{user.email}</p>
								</div>
							</div>
						</div>
					)
					: ''}
				<hr className="continue-as-page__divider" />
				<div>
					<AutoFormattedMessage
						id="page.continueAs.notRightAccountDescription"
					/>
					<SecondaryCTA className="continue-as-page__secondary-cta">
						<Link
							to="/login?hidePageView=true"
							onClick={useDifferentAccount}
						>
							<AutoFormattedMessage
								id="page.continueAs.linkToLoginPage"
							/>
						</Link>
					</SecondaryCTA>
					<div className="continue-as-page__privacy-terms">
						<AutoFormattedMessage
							id="page.continueAs.term"
							values={{
								link: (
									<Link
										to={`https://www.loblaw.ca/${parseLanguage(locale)}/privacy-policy/`}
										target="_blank"
									>
										<AutoFormattedMessage id="page.continueAs.termLink" />
									</Link>
								),
							}}
						/>
					</div>
				</div>
			</Layout>
		);
	}
};

ContinueAsPage.propTypes = {
	locale: PropTypes.string,
};

export default withLocale(ContinueAsPage);
